import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';
import { RevealPhotos } from './RevealPhotos';
import { useRevealViewport, PreparingIndicator } from '../useRevealViewport';
import type { FullCanvasSceneProps } from './types';
import { STYLE_ALIGNMENT } from './types';
import { AdaptiveMessageBody } from '../cinematic/adaptive/AdaptiveMessageBody';
import { getReadingRevealCompleteMs } from './readingDwell';

import { DepthRevealLayer } from '../cinematic/DepthRevealLayer';
const EASE = [0.22, 0.61, 0.36, 1] as const;

export function FullCanvasKeepsake({ recipientName, relationship, message, photoUrls, publishedDate, onComplete, config, startAtMessage = false }: FullCanvasSceneProps) {
  const [phase, setPhase] = useState<'corners' | 'borders' | 'resolve' | 'text' | 'done'>(startAtMessage ? 'text' : 'corners');
  const { isMobile, burstOriginY, timingMultiplier: tm, particleScale } = useRevealViewport();

  const t = (ms: number) => ms * tm;

  useEffect(() => {
    if (startAtMessage) {
      const tDone = setTimeout(() => { setPhase('done'); onComplete(); }, getReadingRevealCompleteMs({
        message,
        mode: config?.style.readingRhythm ?? 'sentence',
        pacing: config?.pacing ?? 1,
        startDelay: 0.4,
      }));
      return () => clearTimeout(tDone);
    }
    const t1 = setTimeout(() => setPhase('borders'), t(4000));
    const t2 = setTimeout(() => setPhase('resolve'), t(7500));
    const t3 = setTimeout(() => setPhase('text'), t(11500));
    const t4 = setTimeout(() => { setPhase('done'); onComplete(); }, t(17000));
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [config?.pacing, config?.style.readingRhythm, message, onComplete, startAtMessage, tm]);

  const isPostCorners = phase !== 'corners';
  const isResolveOrLater = phase === 'resolve' || phase === 'text' || phase === 'done';
  const isTextVisible = phase === 'text' || phase === 'done';

  const goldDust = useMemo(() =>
    Array.from({ length: Math.round(25 * particleScale) }, (_, i) => ({
      x: 10 + Math.random() * 80, y: 10 + Math.random() * 80,
      size: 1 + Math.random() * 2.5, duration: 2 + Math.random() * 3,
      delay: t(8000) / 1000 + Math.random() * 3, brightness: 0.3 + Math.random() * 0.6,
    })),
  [particleScale, tm]);

  const corners = [
    { pos: 'top-4 left-4 md:top-8 md:left-8', rotate: '0deg', delay: 0.3 },
    { pos: 'top-4 right-4 md:top-8 md:right-8', rotate: '90deg', delay: 0.8 },
    { pos: 'bottom-4 right-4 md:bottom-8 md:right-8', rotate: '180deg', delay: 1.3 },
    { pos: 'bottom-4 left-4 md:bottom-8 md:left-8', rotate: '270deg', delay: 1.8 },
  ];

  return (
    <div className={`min-h-[100dvh] sm:min-h-screen relative ${isTextVisible ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden'}`}>
      <motion.div className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a0610 0%, #150d20 40%, #0a0610 100%)' }}
        animate={{ opacity: isResolveOrLater ? 0 : 1 }}
        transition={{ duration: 3.0, ease: EASE as any }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50/80 to-indigo-50/60" />
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Mobile preparing indicator */}
      {isMobile && <PreparingIndicator visible={phase === 'corners'} color="rgba(167,139,250,0.7)" />}

      <motion.div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(167,139,250,0.12) 0%, transparent 50%)' }}
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ scale: isResolveOrLater ? 1.5 : 0.3, opacity: isResolveOrLater ? 1 : 0 }}
        transition={{ duration: 3, ease: 'easeOut' }}
      />

      {corners.map(({ pos, rotate, delay }, i) => (
        <motion.div key={i} className={`absolute ${isMobile ? 'w-16 h-16' : 'w-24 h-24'} md:w-36 md:h-36 pointer-events-none z-30 ${pos}`}
          style={{ transform: `rotate(${rotate})` }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 0.9, 0.7], scale: [0, 1.3, 1] }}
          transition={{ delay, duration: 1.2, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <svg viewBox="0 0 60 60" className="w-full h-full">
            <defs>
              <linearGradient id={`gold-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgba(251,191,36,0.8)" />
                <stop offset="50%" stopColor="rgba(217,159,53,0.6)" />
                <stop offset="100%" stopColor="rgba(180,130,20,0.4)" />
              </linearGradient>
            </defs>
            <path d="M5 5 Q5 30 30 30 Q5 30 5 55" stroke={`url(#gold-${i})`} strokeWidth="2" fill="none" />
            <path d="M12 5 Q12 22 30 22" stroke={`url(#gold-${i})`} strokeWidth="1" fill="none" opacity="0.6" />
            <path d="M5 12 Q22 12 22 30" stroke={`url(#gold-${i})`} strokeWidth="1" fill="none" opacity="0.6" />
            <circle cx="8" cy="8" r="4" fill="rgba(251,191,36,0.5)" />
            <circle cx="8" cy="8" r="2" fill="rgba(251,191,36,0.8)" />
            <path d="M15 8 Q20 15 15 22" stroke="rgba(251,191,36,0.3)" strokeWidth="0.5" fill="none" />
            <path d="M8 15 Q15 20 22 15" stroke="rgba(251,191,36,0.3)" strokeWidth="0.5" fill="none" />
            <circle cx="18" cy="18" r="1.5" fill="rgba(251,191,36,0.4)" />
          </svg>
          <motion.div className="absolute inset-0"
            style={{ background: 'radial-gradient(circle at 15% 15%, rgba(251,191,36,0.2) 0%, transparent 50%)' }}
            animate={{ opacity: [0.3, 0.8, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: delay + 1 }}
          />
        </motion.div>
      ))}

      {isPostCorners && (
        <div className="absolute inset-4 md:inset-8 z-20 pointer-events-none">
          <motion.div className="absolute top-0 left-12 right-12 md:left-18 md:right-18 h-px"
            style={{ background: 'linear-gradient(to right, rgba(251,191,36,0.6), rgba(217,159,53,0.3), rgba(251,191,36,0.6))' }}
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 1.5, delay: 0.2, ease: EASE as any }}
          />
          <motion.div className="absolute bottom-0 left-12 right-12 md:left-18 md:right-18 h-px"
            style={{ background: 'linear-gradient(to right, rgba(251,191,36,0.6), rgba(217,159,53,0.3), rgba(251,191,36,0.6))' }}
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
            transition={{ duration: 1.5, delay: 0.6, ease: EASE as any }}
          />
          <motion.div className="absolute left-0 top-12 bottom-12 md:top-18 md:bottom-18 w-px"
            style={{ background: 'linear-gradient(to bottom, rgba(251,191,36,0.6), rgba(217,159,53,0.3), rgba(251,191,36,0.6))' }}
            initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
            transition={{ duration: 1.5, delay: 1.0, ease: EASE as any }}
          />
          <motion.div className="absolute right-0 top-12 bottom-12 md:top-18 md:bottom-18 w-px"
            style={{ background: 'linear-gradient(to bottom, rgba(251,191,36,0.6), rgba(217,159,53,0.3), rgba(251,191,36,0.6))' }}
            initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
            transition={{ duration: 1.5, delay: 1.4, ease: EASE as any }}
          />
        </div>
      )}

      <motion.div className="absolute top-0 bottom-0 w-px pointer-events-none z-25"
        style={{
          background: 'linear-gradient(to bottom, transparent 10%, rgba(251,191,36,0.5) 50%, transparent 90%)',
          boxShadow: '0 0 40px 15px rgba(251,191,36,0.15)',
        }}
        initial={{ left: '-5%', opacity: 0 }}
        animate={isTextVisible ? { left: '105%', opacity: [0, 1, 1, 0] } : {}}
        transition={{ duration: 2.0, ease: EASE as any }}
      />

      {isResolveOrLater && goldDust.map((d, i) => (
        <motion.div key={`dust-${i}`} className="absolute rounded-full pointer-events-none"
          style={{
            width: d.size, height: d.size, left: `${d.x}%`, top: `${d.y}%`,
            background: 'rgba(251,191,36,0.6)',
            boxShadow: `0 0 ${d.size * 4}px ${d.size * 2}px rgba(251,191,36,0.2)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, d.brightness, 0], y: [0, -15, 0] }}
          transition={{ duration: d.duration, delay: d.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      <motion.div
        className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8"
        initial={{ filter: 'blur(20px)', opacity: 0 }}
        animate={
          isTextVisible ? { filter: 'blur(0px)', opacity: 1 }
            : isResolveOrLater ? { filter: 'blur(6px)', opacity: 0.4 } : {}
        }
        transition={{ duration: startAtMessage ? 1.9 : 2.5, ease: EASE as any }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-violet-500/60 mb-3">A treasured message</p>
          <h1 className="font-serif text-3xl md:text-5xl text-violet-900/90 mb-4 tracking-wide">For {recipientName}</h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-violet-300/40" />
            <motion.div className="w-2 h-2 rounded-full bg-amber-400/50"
              animate={{ boxShadow: ['0 0 0 0 rgba(251,191,36,0)', '0 0 12px 4px rgba(251,191,36,0.4)', '0 0 0 0 rgba(251,191,36,0)'] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            />
            <div className="w-16 h-px bg-violet-300/40" />
          </div>

          <RevealPhotos photoUrls={photoUrls} visible={isTextVisible} delay={0.5} borderColor="rgba(167,139,250,0.5)" shadowColor="rgba(100,80,200,0.15)" />

          <div className="mb-12">
            <AdaptiveMessageBody
              message={message}
              mode={config?.style.readingRhythm ?? 'sentence'}
              pacing={config?.pacing ?? 1}
              visible={isTextVisible}
              startDelay={0.4}
              alignment={config ? STYLE_ALIGNMENT[config.styleId] : 'centered'}
              textColorClass="text-violet-900/85"
              emphasisGlow="rgba(196,181,253,0.5)"
              typography={config?.typography ?? "serif-classic"}
            />
          </div>

          <p className="text-lg font-serif italic text-violet-700/70">With cherished memory,</p>
          <p className="text-xl font-serif text-violet-800/80 mt-1">— From your {getSenderPerspectiveLabel(relationship)}</p>
          <div className="mt-10 pt-6 border-t border-violet-200/30">
            <p className="text-xs text-violet-500/40 tracking-wider">Say It NOW • While They're Still Here</p>
            {publishedDate && <p className="text-xs text-violet-400/30 mt-1">Shared on {publishedDate}</p>}
          </div>
        </div>
      </motion.div>
      <DepthRevealLayer active={isTextVisible} tint="rgba(167,139,250,0.55)" glow="rgba(196,181,253,0.5)" />
    </div>
  );
}
